import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";

// These endpoints return the raw Zod objects (snake_case), so the frontend
// types mirror that exactly — self-contained, no mapping layer.

export type ConsentMode = "banner" | "externalCMP" | "off";
export type DeniedStage = "A" | "B" | "C";
export type ConsentCategory = "necessary" | "functional" | "statistics" | "marketing";
export type DestinationType = "dhctrack" | "meta" | "google" | "google_ads" | "linkedin" | "custom";
export type FingerprintMode = "off" | "consent" | "always";

export interface BypassRules {
  urls: string[];
  codes: string[];
}

/** Server message of a rejected save (e.g. `pixel_confirm_invalid`), else the generic error text. */
export function trackingSaveErrorText(e: unknown): string {
  const body = e instanceof ApiError ? (e.body as { message?: unknown } | undefined) : undefined;
  return typeof body?.message === "string" ? body.message : (e as Error).message;
}

export interface EventRule {
  match: "contains" | "exact";
  url: string;
  event_type: string;
  browser: boolean;
  server: boolean;
  /** Per-rule Google destination toggles (TASK-83). Absent = on (server-side
   *  default — see matchEventRules in apps/worker/src/processors/capi-dispatch.ts). */
  google_ads?: boolean;
  google_ga4?: boolean;
  /** TASK-236 (pilot): pixel only after the server confirms a server-side HQ lead. Absent = off. */
  pixel_confirm?: boolean;
  /** TASK-254 (S15): "An Meta nur HQ-Leads als Lead melden". Absent = off (existing rules unchanged). */
  meta_hq_only?: boolean;
}

/**
 * dhc event_type → Meta/Google-Ads-stage name. Mirrors metaEventName in
 * packages/identity/src/conversion-dedup.ts (the worker's capi-dispatch.ts
 * re-uses it to key project_tracking_config.event_rules against a Google Ads
 * destination's extra_config.conversion_actions — see
 * STANDARD_CONVERSION_ACTIONS in apps/api/src/lib/google-ads.ts: "stage keys
 * mirror metaEventName() so the worker can route by stage"). Admin is a
 * separate browser bundle with no runtime dependency on the identity package,
 * so this is a deliberate duplicate; apps/admin/tests/meta-event-name.test.ts
 * runs a table against the real function and fails on drift.
 */
export function metaEventName(eventType: string): string {
  switch (eventType) {
    case "Purchase":
      return "Purchase";
    case "Lead":
    case "HQLead":
    case "LeadNewCustomer":
      return "Lead";
    case "HQLeadRegistration":
    case "CompleteRegistration":
      return "CompleteRegistration";
    case "Schedule":
    case "WebinarAttendance":
      return "Schedule";
    case "Contact":
      return "Contact";
    case "PageView":
      return "PageView";
    default:
      return eventType;
  }
}

/**
 * TASK-83 Station-5 fix — hint text for a rule whose Google Ads / GA4 switch
 * is off. The worker only suppresses a destination when EVERY matching rule
 * for an event agrees (`matchedRules.every(r => r.google_ads === false)` in
 * apps/worker/src/processors/capi-dispatch.ts's dispatchCapi), so a single
 * off rule doesn't mean the event never reaches that target. This checks
 * only `event_type` (not the worker's URL-overlap matchEventRules) — cheaper
 * and deliberately conservative: a caveat shown too often beats a false "it
 * won't send" reading.
 */
export function googleToggleOffHint(
  label: "Google Ads" | "GA4",
  field: "google_ads" | "google_ga4",
  eventType: string,
  rules: Pick<EventRule, "event_type" | "google_ads" | "google_ga4">[],
): string {
  const base = `→ ${label}: für diese Regel aus`;
  const otherRuleStillOn = rules.some((r) => r.event_type === eventType && r[field] !== false);
  return otherRuleStillOn
    ? `${base} — eine andere Regel für „${eventType}“ kann trotzdem senden`
    : base;
}

export interface BannerConfig {
  primary_color?: string;
  position?: "bottom" | "top" | "center" | "cloud";
  layout?: "box" | "bar" | "cloud";
  default_lang?: "de" | "en";
  logo_url?: string;
  [k: string]: unknown;
}

export interface TrackingConfig {
  project_id: number;
  funnel_mark: string;
  consent_mode: ConsentMode;
  consent_mode_v2: boolean;
  default_denied_stage: DeniedStage;
  categories: ConsentCategory[];
  bypass_rules: BypassRules;
  banner_config: BannerConfig;
  legal_text_ref: string | null;
  policy_version: number;
  browser_conversion_event: string | null;
  fingerprint_mode: FingerprintMode;
  bot_gate_enabled: boolean;
  cookie_autoblock_enabled: boolean;
  meta_event_allowlist: string[] | null;
  meta_event_values: Record<string, { value: number; currency?: string }> | null;
  event_rules: EventRule[];
  capi_merge_buffer_seconds: number | null;
  new_customer_event_types: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface TrackingConfigInput {
  consent_mode?: ConsentMode;
  consent_mode_v2?: boolean;
  default_denied_stage?: DeniedStage;
  categories?: ConsentCategory[];
  bypass_rules?: BypassRules;
  banner_config?: BannerConfig;
  legal_text_ref?: string | null;
  policy_version?: number;
  browser_conversion_event?: string | null;
  fingerprint_mode?: FingerprintMode;
  bot_gate_enabled?: boolean;
  cookie_autoblock_enabled?: boolean;
  meta_event_allowlist?: string[] | null;
  meta_event_values?: Record<string, { value: number; currency?: string }> | null;
  event_rules?: EventRule[];
  capi_merge_buffer_seconds?: number | null;
  new_customer_event_types?: string[] | null;
  risk_acknowledged?: boolean;
}

export interface TrackingDestination {
  id: number;
  project_id: number;
  funnel_mark: string;
  type: DestinationType;
  label: string | null;
  client_id: string | null;
  tag_snippet: string | null;
  has_credentials: boolean;
  extra_config: Record<string, unknown>;
  ad_account_ref: string | null;
  category: ConsentCategory;
  denied_stage: DeniedStage;
  bypass_rules: BypassRules;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrackingDestinationInput {
  type?: DestinationType;
  label?: string | null;
  client_id?: string | null;
  tag_snippet?: string | null;
  server_credential?: string | null;
  extra_config?: Record<string, unknown>;
  ad_account_ref?: string | null;
  category?: ConsentCategory;
  denied_stage?: DeniedStage;
  bypass_rules?: BypassRules;
  active?: boolean;
  risk_acknowledged?: boolean;
}

export interface AdAccount {
  id: string;
  provider: string | null;
  accountId: string | null;
  name: string | null;
}

export interface AuditEntry {
  id: number;
  destination_id: number | null;
  actor_email: string;
  action: string;
  from_value: string | null;
  to_value: string | null;
  risk_acknowledged: boolean;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const configKey = (id: number) => ["admin", "projects", "tracking-config", id] as const;
const destKey = (id: number) => ["admin", "projects", "destinations", id] as const;
const adAccountsKey = (id: number) => ["admin", "projects", "ad-accounts", id] as const;
const auditKey = (id: number) => ["admin", "projects", "tracking-audit", id] as const;

// --- tracking config ------------------------------------------------------

export function useTrackingConfig(id: number) {
  return useQuery<{ config: TrackingConfig; secretsConfigured: boolean }>({
    queryKey: configKey(id),
    queryFn: () => api(`/admin/projects/${id}/tracking-config`),
    staleTime: 30_000,
  });
}

export function useUpdateTrackingConfig(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TrackingConfigInput) =>
      api<{ ok: true; config: TrackingConfig }>(
        `/admin/projects/${id}/tracking-config`,
        { method: "PUT", body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKey(id) });
      qc.invalidateQueries({ queryKey: auditKey(id) });
    },
  });
}

// --- destinations ---------------------------------------------------------

export function useDestinations(id: number) {
  return useQuery<{ destinations: TrackingDestination[]; secretsConfigured: boolean }>({
    queryKey: destKey(id),
    queryFn: () => api(`/admin/projects/${id}/destinations`),
    staleTime: 30_000,
  });
}

export function useCreateDestination(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TrackingDestinationInput) =>
      api<{ ok: true; destination: TrackingDestination }>(
        `/admin/projects/${id}/destinations`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: destKey(id) });
      qc.invalidateQueries({ queryKey: auditKey(id) });
    },
  });
}

export function useUpdateDestination(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ destId, input }: { destId: number; input: TrackingDestinationInput }) =>
      api<{ ok: true; destination: TrackingDestination }>(
        `/admin/projects/${id}/destinations/${destId}`,
        { method: "PUT", body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: destKey(id) });
      qc.invalidateQueries({ queryKey: auditKey(id) });
    },
    // Refetch on failure too: a 404 means the cached row is stale (deleted
    // elsewhere) — pull the fresh list so the phantom row disappears.
    onError: () => {
      qc.invalidateQueries({ queryKey: destKey(id) });
    },
  });
}

export function useDeleteDestination(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (destId: number) =>
      api<{ ok: true }>(`/admin/projects/${id}/destinations/${destId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: destKey(id) });
      qc.invalidateQueries({ queryKey: auditKey(id) });
    },
  });
}

export interface MetaTestResult {
  ok: boolean;
  status: number;
  events_received?: number;
  fbtrace_id?: string;
  error?: string;
}

/** Send a Meta CAPI test event (requires the destination to have a test_event_code). */
export function useTestDestination(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (destId: number) =>
      api<MetaTestResult>(`/admin/projects/${id}/destinations/${destId}/test`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: auditKey(id) }),
  });
}

export interface EmqEvent {
  event_name: string;
  composite_score: number | null;
  match_keys: Array<{ identifier: string; coverage: number }>;
  diagnostics?: Array<{ name: string; description: string; percentage: number | null }>;
}
export interface EmqResult {
  ok: boolean;
  events?: EmqEvent[];
  error?: string;
}

export interface GoogleAdsWebConversionsResult {
  ok: boolean;
  /** stage → website conversion (action + snippet target) stored on the destination. */
  web_conversions: Record<string, { action: string; send_to: string }>;
  /** Stages for which Google delivered no snippet target — not marked as website stages. */
  missing: string[];
  error?: string;
}

/** Result of the button plus the destination's fresh `extra_config` (null = could not be re-read). */
export type GoogleAdsWebConversionsOutcome = GoogleAdsWebConversionsResult & {
  fresh_extra_config: Record<string, unknown> | null;
};

/** Create/link the Google Ads website (WEBPAGE) conversions (TASK-243). */
export function useGoogleAdsWebConversions(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (destId: number): Promise<GoogleAdsWebConversionsOutcome> => {
      const result = await api<GoogleAdsWebConversionsResult>(
        `/admin/projects/${id}/destinations/${destId}/google-ads/web-conversions`,
        { method: "POST" },
      );
      // The button rewrote the stage mapping server-side: re-read it, so an open
      // edit form / mapping panel can be refilled (else a later save would put
      // the stale mapping back). Re-read failure → null → caller closes them.
      const fresh = await qc
        .fetchQuery({
          queryKey: destKey(id),
          queryFn: () => api<{ destinations: TrackingDestination[] }>(`/admin/projects/${id}/destinations`),
          staleTime: 0,
        })
        .then((r) => r.destinations.find((d) => d.id === destId)?.extra_config ?? null)
        .catch(() => null);
      return { ...result, fresh_extra_config: fresh };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: destKey(id) }),
  });
}

// Keys the Google Ads web-conversions route rewrites. A superset of the
// SQL-protected ones (tag_id, web_conversions): conversion_actions stays
// user-editable, but the button changes it too, so an open form must be refilled.
const SETUP_REWRITTEN_KEYS = ["conversion_actions", "web_conversions", "tag_id"] as const;

/** `current` (an open form's extra_config) with the keys the server rewrites
 *  taken from `fresh`; the user's other edits stay. */
export function withFreshServerConfig(
  current: Record<string, unknown>,
  fresh: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...current };
  for (const k of SETUP_REWRITTEN_KEYS) {
    if (k in fresh) out[k] = fresh[k];
    else delete out[k];
  }
  return out;
}

/** The stage → conversion-action mapping stored on a Google Ads destination. */
export function conversionMapping(extraConfig: Record<string, unknown> | null | undefined): Record<string, string> {
  const m = extraConfig?.["conversion_actions"];
  return typeof m === "object" && m !== null && !Array.isArray(m) ? { ...(m as Record<string, string>) } : {};
}

/**
 * Stages whose stored website conversion is still valid: the stage mapping
 * must point at exactly that conversion. Deliberate copy of
 * activeWebConversion (packages/identity/src/google-web-conversion.ts) — the
 * admin bundle can't import the package; web-conversion-notice.test.ts pins parity.
 */
export function webConversionStages(extraConfig: Record<string, unknown> | null | undefined): string[] {
  const rec = (v: unknown): Record<string, unknown> | null =>
    typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  const web = rec(extraConfig?.["web_conversions"]);
  const mapped = rec(extraConfig?.["conversion_actions"]);
  return ["Lead", "Contact", "Purchase"].filter((stage) => {
    const stored = rec(web?.[stage]);
    const action = stored?.["action"];
    const sendTo = stored?.["send_to"];
    return (
      typeof action === "string" &&
      typeof sendTo === "string" &&
      /^AW-\d+\/[\w-]+$/.test(sendTo) &&
      mapped?.[stage] === action
    );
  });
}

export interface GoogleAdsAccountAction {
  resourceName: string;
  name: string;
}
export interface GoogleAdsActionsResult {
  ok: boolean;
  actions: GoogleAdsAccountAction[];
  error?: string;
}

export interface GoogleAdsAccount {
  id: string;
  name: string;
}

/** Client accounts under the DHC MCC — for the customer-id picker. */
export function useGoogleAdsAccounts(enabled: boolean) {
  return useQuery<{ ok: boolean; accounts: GoogleAdsAccount[]; error?: string }>({
    queryKey: ["admin", "google-ads", "accounts"],
    queryFn: () => api(`/admin/google-ads/accounts`),
    staleTime: 300_000,
    retry: false,
    enabled,
  });
}

/** List the ENABLED conversion actions of the linked Google Ads account. */
export function useGoogleAdsConversionActions(id: number) {
  return useMutation({
    mutationFn: (destId: number) =>
      api<GoogleAdsActionsResult>(
        `/admin/projects/${id}/destinations/${destId}/google-ads/conversion-actions`,
      ),
  });
}

/** Fetch Meta's Event Match Quality (EMQ) for a Meta destination on demand. */
export function useDestinationEmq(id: number) {
  return useMutation({
    mutationFn: (destId: number) =>
      api<EmqResult>(`/admin/projects/${id}/destinations/${destId}/emq`),
  });
}

// CPL suggestion (TASK-24 / #152, testweise): 80% of the trailing 90-day
// average CPL from Meta Insights, for the Event-Routing table's lead-value
// fields. Fail-soft — always ok:false + a distinct error code, never a thrown
// error the mutation would surface as `isError`.
export type CplSuggestionResult =
  | { ok: true; cpl: number; suggestion: number; spend: number; leads: number; window: "last_90d" }
  | { ok: false; error: "no_ad_account" | "not_configured" | "meta_error" | "no_leads"; message?: string };

/** Fetch the CPL suggestion for a Meta destination on demand. */
export function useCplSuggestion(id: number) {
  return useMutation({
    mutationFn: (destId: number) =>
      api<CplSuggestionResult>(`/admin/projects/${id}/destinations/${destId}/cpl-suggestion`),
  });
}

// --- Meta System User (agency-global pixel + ad-account picker) ------------

export interface MetaPixel {
  id: string;
  name: string;
}
export interface MetaSystemAdAccount {
  account_id: string;
  name: string;
}

/** Pixels from the connected Business Manager. 503 → system user not set up. */
export function useMetaPixels() {
  return useQuery<{ pixels: MetaPixel[] }>({
    queryKey: ["admin", "meta", "pixels"],
    queryFn: () => api(`/admin/meta/pixels`),
    staleTime: 300_000,
    retry: false,
  });
}

export interface PixelCapiCheck {
  ok: boolean;
  needsToken: boolean;
  error?: string;
}

/** Probe whether the system-user token can already send CAPI for a pixel. */
export function useMetaPixelCapiCheck() {
  return useMutation({
    mutationFn: (pixelId: string) =>
      api<PixelCapiCheck>(`/admin/meta/pixels/${encodeURIComponent(pixelId)}/capi-check`),
  });
}

export function useMetaSystemAdAccounts() {
  return useQuery<{ accounts: MetaSystemAdAccount[] }>({
    queryKey: ["admin", "meta", "ad-accounts"],
    queryFn: () => api(`/admin/meta/ad-accounts`),
    staleTime: 300_000,
    retry: false,
  });
}

// --- ad accounts + audit log ----------------------------------------------

export function useAdAccounts(id: number) {
  return useQuery<{ accounts: AdAccount[]; linked: boolean }>({
    queryKey: adAccountsKey(id),
    queryFn: () => api(`/admin/projects/${id}/ad-accounts`),
    staleTime: 60_000,
  });
}

export function useTrackingAuditLog(id: number) {
  return useQuery<{ entries: AuditEntry[] }>({
    queryKey: auditKey(id),
    queryFn: () => api(`/admin/projects/${id}/tracking-audit-log`),
    staleTime: 30_000,
  });
}

export interface DeliverySummary {
  total_fired: number;
  total_blocked: number;
  delivery_rate: number;
  by_day: Array<{ day: string; fired: number; blocked: number }>;
}

export function usePixelDelivery(id: number) {
  return useQuery<DeliverySummary>({
    queryKey: ["admin", "projects", "pixel-delivery", id],
    queryFn: () => api(`/admin/projects/${id}/pixel-delivery`),
    staleTime: 60_000,
  });
}

export interface CapiDeliverySummary {
  total_sent: number;
  total_failed: number;
  delivery_rate: number;
  by_day: Array<{ day: string; sent: number; failed: number }>;
}

export function useCapiDelivery(id: number) {
  return useQuery<CapiDeliverySummary>({
    queryKey: ["admin", "projects", "capi-delivery", id],
    queryFn: () => api(`/admin/projects/${id}/capi-delivery`),
    staleTime: 60_000,
  });
}

export interface CookieFinding {
  kind: "cookie" | "tracker";
  id: string;
  category: "necessary" | "functional" | "statistics" | "marketing";
  provider: string;
  description: string;
}
export interface CookieScan {
  scanned_url: string;
  status: "pending" | "done" | "failed";
  findings: CookieFinding[];
  error: string | null;
  scanned_at: string | null;
}
export interface DismissedFinding {
  kind: "cookie" | "tracker";
  id: string;
  dismissed_at: string;
}
export interface CookieScanResponse {
  scan: CookieScan | null;
  dismissed: DismissedFinding[];
}

export function useCookieScan(id: number) {
  return useQuery<CookieScanResponse>({
    queryKey: ["admin", "projects", "cookie-scan", id],
    queryFn: () => api(`/admin/projects/${id}/cookie-scan`),
    refetchInterval: (q) =>
      (q.state.data as CookieScanResponse | undefined)?.scan?.status === "pending" ? 4000 : false,
  });
}

export function useTriggerCookieScan(id: number) {
  const qc = useQueryClient();
  return useMutation({
    // Pass a URL to scan one page, or null/"" to scan the project's allowed domains.
    mutationFn: (url?: string) =>
      api<{ ok: true }>(`/admin/projects/${id}/cookie-scan`, {
        method: "POST",
        body: JSON.stringify(url ? { url } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects", "cookie-scan", id] }),
  });
}

/** Dismiss (hide + remember) or restore a single scan finding. */
export function useDismissFinding(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (f: { kind: "cookie" | "tracker"; id: string }) =>
      api<{ ok: true }>(`/admin/projects/${id}/cookie-scan/dismiss`, {
        method: "POST",
        body: JSON.stringify(f),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects", "cookie-scan", id] }),
  });
}

export function useRestoreFinding(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (f: { kind: "cookie" | "tracker"; id: string }) =>
      api<{ ok: true }>(`/admin/projects/${id}/cookie-scan/restore`, {
        method: "POST",
        body: JSON.stringify(f),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects", "cookie-scan", id] }),
  });
}

export interface BotFilterSummary {
  configured: boolean;
  total_seen: number;
  total_allowed: number;
  total_blocked: number;
  block_rate: number;
}

export function useBotFilter(id: number) {
  return useQuery<BotFilterSummary>({
    queryKey: ["admin", "projects", "bot-filter", id],
    queryFn: () => api(`/admin/projects/${id}/bot-filter`),
    staleTime: 60_000,
  });
}

export interface InboundSource {
  id: number;
  signature: string;
  transport: string;
  event_type: string | null;
  detected_platform: string | null;
  client_ua: string | null;
  sample_payloads: Array<{ at: string; payload: unknown }>;
  event_count: number;
  first_seen: string;
  last_seen: string;
}

export function useInboundSources(id: number) {
  return useQuery<{ sources: InboundSource[] }>({
    queryKey: ["admin", "projects", "inbound-sources", id],
    queryFn: () => api(`/admin/projects/${id}/inbound-sources`),
    staleTime: 30_000,
  });
}
